import { 
  collection, doc, getDoc, getDocs, addDoc, setDoc, 
  updateDoc, query, orderBy, serverTimestamp,
  onSnapshot, where, deleteDoc, Timestamp
} from 'firebase/firestore';
import moment from 'moment';

// Helper function to safely convert Firestore timestamp to ISO string
function safeTimestampToISO(timestamp) {
  if (!timestamp) return new Date().toISOString();
  
  try {
    // Handle Firestore timestamp objects
    if (timestamp instanceof Timestamp || (timestamp && typeof timestamp.toDate === 'function')) {
      return timestamp.toDate().toISOString();
    }
    
    // Handle Date objects
    if (timestamp instanceof Date) {
      return timestamp.toISOString();
    }
    
    // Handle string or number timestamps
    return new Date(timestamp).toISOString();
  } catch (e) {
    console.warn('Invalid timestamp format:', timestamp, e);
    return new Date().toISOString();
  }
}

function get_time(time) {
  let current_time;
  if (time) {
    current_time = moment(time);
  } else {
    current_time = moment();
  }
  return current_time.format('h:mm A');
}

function get_date_from_now(dateObj, type) {
  const sameDay = type === 'space' ? '[Today]' : 'h:mm A';
  const elseDay = type === 'space' ? 'MMM D, YYYY' : 'DD/MM/YYYY';
  const result = moment(dateObj).calendar(null, {
    sameDay: sameDay,
    lastDay: '[Yesterday]',
    lastWeek: elseDay,
    sameElse: elseDay,
  });
  return result;
}

function is_date_change(dateObj, prevObj) {
  const curDate = moment(dateObj).format('DD/MM/YYYY');
  const prevDate = moment(prevObj).format('DD/MM/YYYY');
  return curDate !== prevDate;
}

function scroll_to_bottom($element) {
  $element.animate(
    {
      scrollTop: $element[0].scrollHeight,
    },
    300
  );
}

function is_image(filename) {
  const allowedExtensions = /(\.jpg|\.jpeg|\.png|\.gif|\.webp)$/i;
  if (!allowedExtensions.exec(filename)) {
    return false;
  }
  return true;
}

async function get_rooms(email) {
  const res = await frappe.call({
    type: 'GET',
    method: 'chat.api.room.get',
    args: {
      email: email,
    },
  });
  return await res.message;
}

async function get_messages(room, email) {
  try {
    // Get messages from Firestore using new syntax
    const messagesRef = collection(frappe.firebase.db, 'messages', room, 'messageList');
    const q = query(messagesRef, orderBy('creation', 'asc'));
    const querySnapshot = await getDocs(q);
    
    const messages = [];
    querySnapshot.forEach((doc) => {
      const message = doc.data();
      messages.push({
        content: message.content,
        sender: message.user,
        creation: message.creation,
        sender_email: message.sender_email
      });
    });
    
    return messages;
  } catch (error) {
    console.error('Error fetching messages:', error);
    // Fallback to Frappe backend if Firestore fails
    const res = await frappe.call({
      method: 'chat.api.message.get_all',
      args: {
        room: room,
        email: email,
      },
    });
    return res.message;
  }
}

async function send_message(content, user, room, email) {
  try {
    // Create a new message in Firestore
    const messagesRef = collection(frappe.firebase.db, 'messages', room, 'messageList');
    const timestamp = new Date().toISOString();
    
    await addDoc(messagesRef, {
      content: content,
      user: user,
      room: room,
      sender_email: email,
      creation: timestamp
    });

    // Update room's last message
    const roomRef = doc(frappe.firebase.db, 'rooms', room);
    await updateDoc(roomRef, {
      'last_message.content': content,
      'last_message.timestamp': serverTimestamp()
    });

    // Also save to Frappe backend for persistence
    await frappe.call({
      method: 'chat.api.message.save_message',
      args: {
        content: content,
        user: user,
        room: room,
        email: email,
      },
    });
  } catch (error) {
    frappe.msgprint({
      title: __('Error'),
      message: __('Something went wrong. Please refresh and try again.'),
    });
  }
}

async function get_settings(token) {
  const res = await frappe.call({
    type: 'GET',
    method: 'chat.api.config.settings',
    args: {
      token: token,
    },
  });
  return await res.message;
}

async function mark_message_read(room) {
  try {
    // Check if this room is already marked as read
    if (frappe.Chat.settings.read_rooms && frappe.Chat.settings.read_rooms[room]) {
      console.log(`Room ${room} already marked as read, skipping count update`);
      return;
    }
    
    // Update read status in Firestore
    const readStatusRef = doc(frappe.firebase.db, 'rooms', room, 'read_status', frappe.session.user);
    await setDoc(readStatusRef, { read: true });
    
    // Track this room as read
    if (!frappe.Chat.settings.read_rooms) {
      frappe.Chat.settings.read_rooms = {};
    }
    frappe.Chat.settings.read_rooms[room] = true;
    
    // Save to localStorage
    localStorage.setItem('chat_read_rooms', JSON.stringify(frappe.Chat.settings.read_rooms));
    
    // Also update in Frappe backend
    await frappe.call({
      method: 'chat.api.message.mark_as_read',
      args: {
        room: room,
      },
    });
  } catch (error) {
    console.error('Error marking message as read:', error);
  }
}

async function create_guest({ email, full_name, message }) {
  const res = await frappe.call({
    method: 'chat.api.user.get_guest_room',
    args: {
      email: email,
      full_name: full_name,
      message: message,
    },
  });
  return await res.message;
}

async function set_typing(room, user, is_typing, is_guest) {
  try {
    await frappe.call({
      method: 'chat.api.message.set_typing',
      args: {
        room: room,
        user: user,
        is_typing: is_typing,
        is_guest: is_guest,
      },
    });
  } catch (error) {
    //pass
  }
}

async function create_private_room(room_name, users, type) {
  try {
    // Create room in Frappe backend
    const res = await frappe.call({
      method: 'chat.api.room.create_private',
      args: {
        room_name: room_name,
        users: users,
        type: type,
      },
    });

    if (!res || !res.message) {
      throw new Error('Failed to create room in backend');
    }

    const roomData = res.message;
    
    // Create room in Firestore
    const roomRef = doc(frappe.firebase.db, 'rooms', roomData.name);
    const firestoreRoom = {
      room_name: room_name,
      type: type,
      members: [frappe.session.user, ...users],
      member_names: roomData.member_names || {},
      created_at: serverTimestamp(),
      last_message: {
        content: '',
        timestamp: serverTimestamp()
      }
    };
    
    await setDoc(roomRef, firestoreRoom);

    // Return combined data
    return {
      name: roomData.name,
      room_name: room_name,
      type: type,
      members: [frappe.session.user, ...users],
      member_names: roomData.member_names || {},
      is_admin: frappe.session.user === 'Administrator',
      created_at: new Date().toISOString()
    };
  } catch (error) {
    console.error('Error creating private room:', error);
    frappe.msgprint({
      title: __('Error'),
      message: __('Failed to create chat room. Please try again.'),
    });
    throw error; // Re-throw to handle in calling function
  }
}

async function set_user_settings(settings) {
  await frappe.call({
    method: 'chat.api.config.user_settings',
    args: {
      settings: settings,
    },
  });
}

function get_avatar_html(room_type, user_email, room_name) {
  let avatar_html;
  if (room_type === 'Direct' && 'desk' in frappe) {
    avatar_html = frappe.avatar(user_email, 'avatar-medium');
  } else {
    avatar_html = frappe.get_avatar('avatar-medium', room_name);
  }
  return avatar_html;
}

function set_notification_count(type, roomId) {
  // Get current count
  const current_count = frappe.Chat.settings.unread_count || 0;
  let newCount = current_count;
  
  console.log(`Notification count action: ${type}, current count: ${current_count}, room: ${roomId || 'none'}`);
  
  if (type === 'increment') {
    // Increment count
    newCount = current_count + 1;
    $('#chat-notification-count').text(newCount);
    console.log(`Incremented count to ${newCount}`);
  } else if (type === 'decrement') {
    // Only decrement if count is greater than 0
    if (current_count > 0) {
      newCount = current_count - 1;
      if (newCount === 0) {
        $('#chat-notification-count').text('');
      } else {
        $('#chat-notification-count').text(newCount);
      }
      console.log(`Decremented count to ${newCount}`);
    }
  } else if (type === 'reset') {
    // Reset for specific room
    // This is used when a room is clicked multiple times
    if (roomId) {
      // Store that this room has been read
      if (!frappe.Chat.settings.read_rooms) {
        frappe.Chat.settings.read_rooms = {};
      }
      frappe.Chat.settings.read_rooms[roomId] = true;
      console.log(`Marked room ${roomId} as read`);
      
      // Save updated read_rooms to localStorage
      localStorage.setItem('chat_read_rooms', JSON.stringify(frappe.Chat.settings.read_rooms));
    }
  }
  
  // Update the global settings with new count
  frappe.Chat.settings.unread_count = newCount;
  
  // Save the current count to localStorage for persistence across reloads
  localStorage.setItem('chat_unread_count', newCount);
}

export {
  get_time,
  scroll_to_bottom,
  get_rooms,
  get_messages,
  get_settings,
  create_guest,
  send_message,
  get_date_from_now,
  is_date_change,
  mark_message_read,
  set_typing,
  is_image,
  create_private_room,
  set_user_settings,
  get_avatar_html,
  set_notification_count,
  safeTimestampToISO,
};
